import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetView,
  type BottomSheetBackdropProps
} from '@gorhom/bottom-sheet'
import { useFocusEffect } from '@react-navigation/native'
import { forwardRef, useCallback } from 'react'
import { Pressable, View } from 'react-native'

import { BigRedButton, Text, VStack } from '@/components'
import { useBottomSheetContainerStyle, useFocusableRef } from '@/hooks'
import {
  redBottomSheetModalBackgroundStyle,
  redBottomSheetModalHandleIndicatorStyle,
  redBottomSheetModalHandleStyle
} from '@/lib'

export type RatingPromptBottomSheetRef = BottomSheetModal

export interface RatingPromptBottomSheetProps {
  onDismiss: () => void
  onNotNow: () => void
  onRate: () => void
}

export const RatingPromptBottomSheet = forwardRef<RatingPromptBottomSheetRef, RatingPromptBottomSheetProps>(
  ({ onDismiss, onNotNow, onRate }, ref) => {
    const [headerRef, focusHeader] = useFocusableRef<View>(true)

    return (
      <BottomSheetModal
        accessible={false}
        accessibilityLiveRegion='assertive'
        accessibilityRole='alert'
        backdropComponent={useCallback(
          (props: BottomSheetBackdropProps) => (
            <BottomSheetBackdrop
              {...props}
              accessible={false}
              accessibilityElementsHidden
              appearsOnIndex={0}
              aria-hidden
              disappearsOnIndex={-1}
              importantForAccessibility='no-hide-descendants'
            />
          ),
          []
        )}
        backgroundStyle={redBottomSheetModalBackgroundStyle}
        containerStyle={useBottomSheetContainerStyle()}
        enableDismissOnClose
        enableDynamicSizing
        enablePanDownToClose
        handleIndicatorStyle={redBottomSheetModalHandleIndicatorStyle}
        handleStyle={redBottomSheetModalHandleStyle}
        onChange={useCallback(
          (position: number) => {
            if (position === 0) {
              focusHeader()
            }
          },
          [focusHeader]
        )}
        onDismiss={onDismiss}
        ref={ref}
        role='alertdialog'
      >
        <BottomSheetView focusHook={useFocusEffect}>
          <VStack className='pb-safe-or-4 flex-1 items-center gap-4 p-4'>
            <View
              accessible
              accessibilityLabel='Enjoying Dateability?'
              focusable
              importantForAccessibility='yes'
              ref={headerRef}
              tabIndex={0}
            >
              <Text
                accessible={false}
                accessibilityElementsHidden
                aria-hidden
                className='text-center font-poppinsSemiBold text-white'
                importantForAccessibility='no-hide-descendants'
                size='lg'
              >
                Enjoying Dateability?
              </Text>
            </View>
            <Text className='text-center text-white'>
              A quick rating helps more people with disabilities find us. Mind leaving one?
            </Text>
            <BigRedButton
              accessibilityLabel='Rate Dateability'
              onPress={onRate}
              variant='outline'
            >
              Rate Dateability
            </BigRedButton>
            <Pressable
              accessibilityLabel='Not right now'
              accessibilityRole='button'
              onPress={onNotNow}
            >
              <Text className='text-center text-white underline'>Not Right Now</Text>
            </Pressable>
          </VStack>
        </BottomSheetView>
      </BottomSheetModal>
    )
  }
)

RatingPromptBottomSheet.displayName = 'RatingPromptBottomSheet'
